import { buildCardMesh, type TMatRef } from '@/infrastructure/engine3d/strategies/card/CardMeshBuilder';
import { CardLightingRig, type TCardDimensions } from '@/infrastructure/engine3d/strategies/card/CardLightingRig';
import { DescriptorTextureApplier, type TTextureSlotDescriptor } from '@/infrastructure/engine3d/textures/DescriptorTextureApplier';
import { EProductCardForm } from '@/domain/product/EProductCardForm';
import { EProductFormat } from '@/domain/product/EProductFormat';
import { loadPaperTextures } from '@/infrastructure/engine3d/strategies/card/PaperTextureLoader';
import { Color, Mesh, MeshStandardMaterial, type Group, type Object3D, type Scene, type Texture } from 'three';
import type { IFoldableStrategy } from '@/application/ports/IFoldableStrategy';
import type { IProduct3DStrategy, TCameraFramingParams } from '@/infrastructure/engine3d/strategies/IProduct3DStrategy';
import type { TCameraFraming } from '@/application/ports/TCameraFraming';
import type { TCanvasSnapshot } from '@/application/ports/TCanvasSnapshot';
import type { TCardMeshParams } from '@/application/ports/TProductMeshParams';
import type { TSide } from '@/domain/shared/TSide';
import type { TStrategyAttachContext } from '@/infrastructure/engine3d/strategies/TStrategyAttachContext';

const CARD_BG_HEX = 0xf8f6f2;
const FOLD_LERP_FACTOR = 0.08;

// Orchestrates the card mesh builder, texture applier and lighting
// rig. Branch (folded vs. cut extrude) lives in the builder/applier.
class CardStrategy implements IFoldableStrategy, IProduct3DStrategy {
    private readonly _lighting = new CardLightingRig();
    private readonly _textureApplier = new DescriptorTextureApplier();
    private _cardDimensions: TCardDimensions | null = null;
    private _fitDistance = 0;
    private _paperNormal: Texture | null = null;
    private _paperRoughness: Texture | null = null;
    private _previousSceneBackground: Scene['background'] = null;
    private _rightPivot: Group | null = null;
    private _root: Group | null = null;
    private _targetFold = 0;
    private _trackedMaterials: MeshStandardMaterial[] = [];
    private readonly _meshParams: TCardMeshParams;

    constructor(meshParams: TCardMeshParams) {
        this._meshParams = meshParams;
    }

    async attach(context: TStrategyAttachContext): Promise<void> {
        const { renderer, scene } = context;

        const paper = await loadPaperTextures(renderer.capabilities.getMaxAnisotropy());
        this._paperNormal = paper.normal;
        this._paperRoughness = paper.roughness;

        // Cream background — restored on detach.
        this._previousSceneBackground = scene.background;
        scene.background = new Color(CARD_BG_HEX);

        const { cardForm, cuttingPathUrl, productFormat, productHeightInMm, productWidthInMm } = this._meshParams;
        const built = await buildCardMesh({
            cardForm,
            cuttingPathUrl,
            paperNormal: paper.normal,
            paperRoughness: paper.roughness,
            productFormat,
            productHeightInMm,
            productWidthInMm,
        });

        this._root = built.root;
        this._rightPivot = built.rightPivot;
        this._trackedMaterials = built.materials;
        this._cardDimensions = built.dimensions;
        this._fitDistance = _computeFitDistance(built.dimensions);
        this._textureApplier.configure(
            cardForm === EProductCardForm.CutShape
                ? _cutShapeDescriptors(built.matRefs, productFormat === EProductFormat.RECTO_VERSO)
                : _foldedDescriptors(built.matRefs, productFormat === EProductFormat.RECTO_VERSO),
        );

        scene.add(this._root);
        this._lighting.attach(scene, built.dimensions);
    }

    applyCanvasTexture(side: TSide, snapshot: TCanvasSnapshot): void {
        this._textureApplier.apply(side, snapshot);
    }

    // Called by the engine after attach() has resolved — _cardDimensions
    // and _fitDistance are always populated at this point.
    getCameraFraming(_params: TCameraFramingParams): TCameraFraming {
        if (!this._cardDimensions) {
            throw new Error('CardStrategy.getCameraFraming called before attach');
        }
        const { cardH } = this._cardDimensions;
        return {
            maxDistance: this._fitDistance * 3,
            maxPolarAngle: Math.PI / 2 - 0.05,
            minDistance: this._fitDistance * 0.4,
            minPolarAngle: 0.1,
            position: { x: 0, y: cardH / 2, z: this._fitDistance },
            target: { x: 0, y: cardH / 2, z: 0 },
        };
    }

    getRoot(): Object3D | undefined {
        return this._root ?? undefined;
    }

    detach(context: TStrategyAttachContext): void {
        const { scene } = context;
        scene.background = this._previousSceneBackground;
        this._previousSceneBackground = null;

        if (this._root) {
            scene.remove(this._root);
            _disposeObjectTree(this._root);
            this._root = null;
        }
        this._lighting.detach(scene);
        this._textureApplier.dispose();

        this._paperNormal?.dispose();
        this._paperRoughness?.dispose();
        this._paperNormal = null;
        this._paperRoughness = null;
        this._trackedMaterials.length = 0;
        this._rightPivot = null;
        this._cardDimensions = null;
    }

    update(): void {
        if (this._rightPivot) {
            this._rightPivot.rotation.y += (this._targetFold - this._rightPivot.rotation.y) * FOLD_LERP_FACTOR;
        }
    }

    setFold(angleRad: number): void {
        this._targetFold = angleRad;
    }
}

// cutShape mode — one ExtrudeGeometry, faceIndex 0 = recto (bottom
// half of cap groups), 2 = verso (top half). Verso mirrors the U axis
// so the card back reads correctly through the paper.
function _cutShapeDescriptors(matRefs: TMatRef[], isRectoVerso: boolean): TTextureSlotDescriptor[] {
    const mesh = matRefs[0]?.mesh;
    if (!mesh) {
        return [];
    }
    const descriptors: TTextureSlotDescriptor[] = [{ mesh, materialIndex: 0, side: 'recto', backgroundFill: 'white' }];
    if (isRectoVerso) {
        descriptors.push({
            mesh,
            materialIndex: 2,
            side: 'verso',
            backgroundFill: 'white',
            uvTransform: { offset: [1, 0], repeat: [-1, 1] },
        });
    }
    return descriptors;
}

// folded mode — 2 BoxGeometry panels (left + right), each face 4 =
// recto / face 5 = verso. Crops expressed as ratios so the descriptor
// stays valid regardless of snapshot dimensions. Verso swaps halves:
// the fabric canvas is authored in reader view but the mesh's verso
// is flipped in scene space, so verso-left samples the RIGHT half of
// the canvas and vice-versa.
const LEFT_HALF = { height: 1, width: 0.5, x: 0, y: 0 } as const;
const RIGHT_HALF = { height: 1, width: 0.5, x: 0.5, y: 0 } as const;

function _foldedDescriptors(matRefs: TMatRef[], isRectoVerso: boolean): TTextureSlotDescriptor[] {
    // Order matches buildCardMesh emission (leftMesh recto, rightMesh recto, ...).
    const leftMesh = matRefs[0]?.mesh;
    const rightMesh = matRefs[1]?.mesh;
    if (!leftMesh || !rightMesh) {
        return [];
    }
    const descriptors: TTextureSlotDescriptor[] = [
        { mesh: leftMesh, materialIndex: 4, side: 'recto', backgroundFill: 'white', sourceCrop: LEFT_HALF },
        { mesh: rightMesh, materialIndex: 4, side: 'recto', backgroundFill: 'white', sourceCrop: RIGHT_HALF },
    ];
    if (isRectoVerso) {
        descriptors.push(
            { mesh: leftMesh, materialIndex: 5, side: 'verso', backgroundFill: 'white', sourceCrop: RIGHT_HALF },
            { mesh: rightMesh, materialIndex: 5, side: 'verso', backgroundFill: 'white', sourceCrop: LEFT_HALF },
        );
    }
    return descriptors;
}

// Kept aspect-agnostic (max of distH/distW) so any resize keeps the
// card safely in frame. Uses the engine's default fov of 40°.
function _computeFitDistance({ cardH, cardW }: { cardH: number; cardW: number }): number {
    const fovRad = (40 * Math.PI) / 180;
    const distH = cardH / 2 / Math.tan(fovRad / 2);
    const distW = cardW / 2 / Math.tan(fovRad / 2);
    return Math.max(distH, distW) * 1.5;
}

function _disposeObjectTree(root: Object3D): void {
    root.traverse((child) => {
        if (child instanceof Mesh) {
            child.geometry?.dispose();
            const { material } = child;
            if (Array.isArray(material)) {
                material.forEach((mat) => {
                    (mat as MeshStandardMaterial).map?.dispose();
                    mat.dispose();
                });
            } else {
                (material as MeshStandardMaterial)?.map?.dispose();
                material?.dispose();
            }
        }
    });
}

export { CardStrategy };
